import { useEffect, useMemo, useState } from "react";
import api from "../api";
import OpenPipelinePanel from "../components/OpenPipelinePanel";

const money = (n) => "$" + Math.round(n || 0).toLocaleString();

const isoDate = (d) => d.toISOString().slice(0, 10);
const DATE_PRESETS = [
  ["Last 30 days", () => { const t = new Date(); const f = new Date(); f.setDate(t.getDate() - 30); return [isoDate(f), isoDate(t)]; }],
  ["This year", () => { const t = new Date(); return [`${t.getFullYear()}-01-01`, isoDate(t)]; }],
  ["Last year", () => { const y = new Date().getFullYear() - 1; return [`${y}-01-01`, `${y}-12-31`]; }],
  ["All time", () => ["", ""]],
];

const TABS = ["Overview", "Clients", "Athletes", "Sports", "Pipeline", "Duplicates", "Invoices"];

const PILL = {
  REAL_LEAD: "#22c55e", UNQUALIFIED: "#f59e0b", SPAM: "#ef4444", INTERNAL: "#8b97a7",
  WEB: "#3b82f6", "AGENCY/RESELLER": "#f59e0b", "DIRECT/REPEAT": "#22c55e",
  "DIRECT/PHONE (unattributed)": "#8b97a7",
};
const STAGE_COLOR = {
  won: "#22c55e", lost: "#ef4444", open: "#f59e0b", excluded: "#8b97a7",
  // HubSpot deal stages (live opportunities), same scheme: amber = waiting on
  // us, blue = in conversation, green = money, red = lost.
  Inquiry: "#f59e0b", "Outreach Done": "#3b82f6", "Quote Sent": "#3b82f6",
  "Contract Out": "#3b82f6", "Deposit Paid": "#14b8a6", Won: "#22c55e", Lost: "#ef4444",
};

// HubSpot is the stage authority for live opportunities; history it does not
// track keeps the cockpit's own won/lost/open.
const withStage = (rows) => (rows || []).map((r) => ({ ...r, stage_view: r.crm_stage || r.stage }));

function Pill({ value }) {
  return (
    <span style={{
      display: "inline-block", padding: "1px 8px", borderRadius: 999, fontSize: 11,
      border: "1px solid var(--line,#2a2f3a)", color: PILL[value] || "inherit", whiteSpace: "nowrap",
    }}>{value}</span>
  );
}

function Bars({ rows, labelKey, valueKey }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey]));
  return (
    <div>
      {rows.map((r) => (
        <div key={r[labelKey]} style={{ display: "flex", alignItems: "center", gap: 10, margin: "6px 0" }}>
          <div style={{ minWidth: 200 }}>{r[labelKey]}</div>
          <div style={{ height: 16, width: `${Math.round(260 * r[valueKey] / max)}px`,
            background: "#3b82f6", borderRadius: 3 }} />
          <div style={{ fontVariantNumeric: "tabular-nums" }}>{money(r[valueKey])}</div>
          {r.deals != null && <div style={{ color: "var(--text-dim,#8b97a7)", fontSize: 12 }}>{r.deals} deals</div>}
        </div>
      ))}
    </div>
  );
}

function DataTable({ rows, columns, initialSort, filters = [] }) {
  const [q, setQ] = useState("");
  const [filterVals, setFilterVals] = useState({});
  const [sortKey, setSortKey] = useState(initialSort?.key || columns[0].key);
  const [sortDir, setSortDir] = useState(initialSort?.dir || -1);

  const view = useMemo(() => {
    let rs = rows.filter((r) => columns.some((c) => String(r[c.key] ?? "").toLowerCase().includes(q.toLowerCase())));
    for (const f of filters) {
      if (filterVals[f.key]) rs = rs.filter((r) => String(r[f.key]) === filterVals[f.key]);
    }
    rs = [...rs].sort((a, b) => {
      const x = a[sortKey], y = b[sortKey];
      if (typeof x === "number" || typeof y === "number") return (((x || 0) - (y || 0))) * sortDir;
      return String(x ?? "").localeCompare(String(y ?? "")) * sortDir;
    });
    return rs;
  }, [rows, columns, q, filterVals, sortKey, sortDir, filters]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => -d);
    else { setSortKey(k); setSortDir(typeof rows[0]?.[k] === "number" ? -1 : 1); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", margin: "10px 0" }}>
        <input className="inp" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}
          style={{ minWidth: 260 }} data-testid="pl-search" />
        {filters.map((f) => {
          const opts = [...new Set(rows.map((r) => r[f.key]).filter((v) => v !== "" && v != null))].sort();
          return (
            <select key={f.key} className="inp" value={filterVals[f.key] || ""}
              onChange={(e) => setFilterVals((v) => ({ ...v, [f.key]: e.target.value }))}>
              <option value="">{`All ${f.label}`}</option>
              {opts.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          );
        })}
        <span style={{ color: "var(--text-dim,#8b97a7)", fontSize: 12 }}>{view.length} rows</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="pl-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} onClick={() => toggleSort(c.key)}
                  style={{ textAlign: c.num ? "right" : "left", padding: "8px 10px", cursor: "pointer",
                    borderBottom: "1px solid var(--line,#2a2f3a)", color: "var(--text-dim,#8b97a7)", whiteSpace: "nowrap" }}>
                  {c.label}{sortKey === c.key ? (sortDir < 0 ? " ▼" : " ▲") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.map((r, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td key={c.key} style={{ textAlign: c.num ? "right" : "left", padding: "8px 10px",
                    borderBottom: "1px solid var(--line,#23272f)", verticalAlign: "top",
                    fontVariantNumeric: c.num ? "tabular-nums" : "normal" }}>
                    {c.render ? c.render(r) : r[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KPI({ label, val, sub }) {
  return (
    <div style={{ border: "1px solid var(--line,#2a2f3a)", borderRadius: 10, padding: "10px 14px", minWidth: 160 }}>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{val}</div>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--text-dim,#8b97a7)" }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--text-dim,#8b97a7)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ProfitabilityTab({ prof, blendedRate, setBlendedRate }) {
  const d = prof?.data;
  if (!prof || !d || !prof.has_contracts) {
    return <div className="disc-empty">No contract-margin data seeded yet. Once contracts are
      loaded, this tab shows confirmed margins plus an estimate over attributed revenue.</div>;
  }
  const rate = blendedRate == null ? d.blended_rate_pct : Number(blendedRate);
  // recompute the blended-rate athletes live; confirmed-rate athletes keep their real rate
  const perAthlete = (d.per_athlete || []).map((p) =>
    p.rate_source === "blended"
      ? { ...p, rate_pct: rate, est_margin: Math.round((p.revenue * rate) / 100),
          est_cost: Math.round(p.revenue - (p.revenue * rate) / 100) }
      : p);
  const estMarginAttr = perAthlete.reduce((s, p) => s + p.est_margin, 0);
  const estMarginBook = Math.round(((prof.total_invoiced || 0) * rate) / 100);
  const c = d.confirmed;

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 8 }}>
        <KPI label="Confirmed margin" val={money(c.margin)}
          sub={`${money(c.revenue)} rev − ${money(c.cost)} cost · ${c.count} deals`} />
        <KPI label="Blended margin rate" val={`${d.blended_rate_pct}%`} sub="from confirmed deals" />
        <KPI label="Est. margin on attributed rev" val={money(estMarginAttr)}
          sub={`on ${money(prof.total_attributed)} attributed`} />
        <KPI label="Est. margin on full book" val={money(estMarginBook)}
          sub={`rough: ${money(prof.total_invoiced)} × rate`} />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "12px 0", fontSize: 13 }}>
        <label>Assumed rate for un-costed deals:</label>
        <input type="number" step="0.5" min="0" max="90" value={rate}
          onChange={(e) => setBlendedRate(e.target.value)} data-testid="prof-rate"
          style={{ width: 70, padding: "4px 8px", borderRadius: 6, border: "1px solid var(--line,#2a2f3a)",
            background: "transparent", color: "inherit" }} />
        <span>%</span>
        <button className="dash-trigger" onClick={() => setBlendedRate(d.blended_rate_pct)}
          style={{ fontSize: 12 }}>reset to blended</button>
      </div>
      <div className="sec-desc" style={{ marginBottom: 16 }}>
        Athletes with a known contract use their <b>confirmed</b> margin rate; everyone else is
        estimated at the assumed rate above. {d.one_sided.revenue_only + d.one_sided.cost_only} contracts
        have only one side known ({d.one_sided.revenue_only} revenue-only, {d.one_sided.cost_only} cost-only).
      </div>

      <div className="sec-head" style={{ fontSize: 15 }}>Confirmed margins (both sides known)</div>
      <DataTable rows={c.deals} initialSort={{ key: "margin", dir: -1 }}
        columns={[
          { key: "athlete", label: "Athlete" },
          { key: "client", label: "Client" },
          { key: "date", label: "Date" },
          { key: "revenue", label: "Revenue", num: true, render: (r) => money(r.revenue) },
          { key: "cost", label: "Athlete cost", num: true, render: (r) => money(r.cost) },
          { key: "margin", label: "Margin", num: true, render: (r) => money(r.margin) },
          { key: "margin_pct", label: "Margin %", num: true, render: (r) => `${r.margin_pct || 0}%` },
        ]} />

      <div className="sec-head" style={{ fontSize: 15, marginTop: 24 }}>Estimated margin by athlete (attributed revenue)</div>
      <DataTable rows={perAthlete} initialSort={{ key: "est_margin", dir: -1 }}
        filters={[{ key: "rate_source", label: "rate source" }]}
        columns={[
          { key: "athlete", label: "Athlete" },
          { key: "sport", label: "Sport" },
          { key: "revenue", label: "Attributed rev", num: true, render: (r) => money(r.revenue) },
          { key: "rate_pct", label: "Rate", num: true, render: (r) => `${r.rate_pct}%` },
          { key: "rate_source", label: "Source", render: (r) => <Pill value={r.rate_source === "confirmed" ? "REAL_LEAD" : "DIRECT/PHONE (unattributed)"} /> },
          { key: "est_margin", label: "Est. margin", num: true, render: (r) => money(r.est_margin) },
          { key: "est_cost", label: "Est. cost", num: true, render: (r) => money(r.est_cost) },
        ]} />
    </div>
  );
}

export default function PipelineDashboard({ goHome, isAdmin }) {
  const [snap, setSnap] = useState(null);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("Overview");
  const [syncing, setSyncing] = useState(false);
  const [err, setErr] = useState("");
  const [prof, setProf] = useState(null);
  const [blendedRate, setBlendedRate] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = (params = {}) => {
    setLoading(true);
    api.get("/pipeline/snapshot", { params }).then(({ data }) => {
      setSnap(data.data);
      setMeta({ syncedAt: data.syncedAt, counts: data.counts, qb_status: data.qb_status });
      setLoading(false); setErr("");
    }).catch((e) => {
      setLoading(false);
      setErr(e?.response?.status === 404 ? "No snapshot yet — run a refresh." : "Failed to load snapshot.");
    });
    api.get("/profitability").then(({ data }) => {
      setProf(data);
      if (data?.data?.blended_rate_pct != null) setBlendedRate(data.data.blended_rate_pct);
    }).catch(() => {});
  };
  useEffect(() => {
    load({ date_from: dateFrom || undefined, date_to: dateTo || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  const TABS_ALL = [...TABS, "Profitability"];

  const refresh = async () => {
    setSyncing(true); setErr("");
    try {
      await api.post("/cron/pipeline-sync");
      load({ date_from: dateFrom || undefined, date_to: dateTo || undefined });
    }
    catch { setErr("Sync failed (check QuickBooks/Strapi config)."); }
    finally { setSyncing(false); }
  };

  const w = snap?.summary?.WEB_CHANNEL || {};
  const kpis = snap ? [
    ["Total invoiced (all channels)", money(snap.total_invoiced)],
    ["Web won", `${money(w.won_invoice_value)} · ${w.won_deals_distinct} deals`],
    ["Real leads (1 per sender/month)", w.real_senders ?? w.real_leads],
    ["Web conversion", `${w.conversion_rate_pct}%`],
    ["Money lost (web est.)", money(w.money_lost_estimate)],
    ["Open web pipeline (estimate)", money(w.open_pipeline_value)],
    ["Clients", snap.clients.length],
    ["Athletes", snap.athletes.length],
    ["Inquiries", `${snap.summary.unique_inquiries} (${snap.summary.by_class.SPAM} spam)`],
  ] : [];

  return (
    <div className="view" data-testid="pipeline-view">
      <div className="crumb">
        <a onClick={goHome} data-testid="pl-back-to-home">← Home</a>{" / "}Revenue &amp; Pipeline
      </div>

      <div className="ver-head" style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
        <div>
          <div className="sec-head" style={{ margin: 0 }}>Revenue &amp; Pipeline</div>
          <div className="sec-desc">
            Website inquiries reconciled to QuickBooks invoices: revenue by client / athlete / sport,
            channel mix, and the searchable lead pipeline.
            {meta.syncedAt && <> Last synced {new Date(meta.syncedAt).toLocaleString()}
              {meta.counts && ` · ${meta.counts.booking + meta.counts.contacts + (meta.counts.site_bookings || 0)} inquiries, ${meta.counts.invoices} invoices`}
              {meta.qb_status && ` · QB: ${meta.qb_status}`}.</>}
          </div>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={refresh} disabled={syncing} data-testid="pl-refresh">
            {syncing ? "Syncing…" : "Refresh now"}
          </button>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", margin: "14px 0 4px" }}>
        <label style={{ fontSize: 12, color: "var(--text-dim,#8b97a7)" }}>From</label>
        <input type="date" className="inp" value={dateFrom} max={dateTo || undefined}
          onChange={(e) => setDateFrom(e.target.value)} data-testid="pl-date-from" />
        <label style={{ fontSize: 12, color: "var(--text-dim,#8b97a7)" }}>To</label>
        <input type="date" className="inp" value={dateTo} min={dateFrom || undefined}
          onChange={(e) => setDateTo(e.target.value)} data-testid="pl-date-to" />
        {(dateFrom || dateTo) && (
          <button className="dash-trigger" style={{ fontSize: 12 }} data-testid="pl-date-clear"
            onClick={() => { setDateFrom(""); setDateTo(""); }}>Clear</button>
        )}
        {DATE_PRESETS.map(([label, fn]) => (
          <button key={label} className="dash-trigger" style={{ fontSize: 12 }}
            onClick={() => { const [f, t] = fn(); setDateFrom(f); setDateTo(t); }}>{label}</button>
        ))}
        {(dateFrom || dateTo) && (
          <span style={{ fontSize: 12, color: "var(--text-dim,#8b97a7)" }}>
            Showing {dateFrom || "the start"} – {dateTo || "now"}
          </span>
        )}
      </div>

      {/* syncedAt moves when a sync stores a new snapshot, so the panel re-reads then. */}
      <OpenPipelinePanel refreshKey={meta.syncedAt} />

      {err && <div className="disc-empty" style={{ color: "#ef4444" }}>{err}</div>}
      {loading ? (
        <div className="disc-empty">Loading…</div>
      ) : !snap ? null : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, margin: "16px 0" }}>
            {kpis.map(([label, val]) => (
              <div key={label} style={{ border: "1px solid var(--line,#2a2f3a)", borderRadius: 10, padding: "10px 14px", minWidth: 150 }}>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{val}</div>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--text-dim,#8b97a7)" }}>{label}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", borderBottom: "1px solid var(--line,#2a2f3a)", marginBottom: 12 }}>
            {TABS_ALL.map((t) => (
              <button key={t} className="dash-trigger" onClick={() => setTab(t)}
                style={{ borderBottom: tab === t ? "2px solid #3b82f6" : "2px solid transparent", opacity: tab === t ? 1 : 0.7 }}
                data-testid={`pl-tab-${t}`}>{t}</button>
            ))}
          </div>

          {tab === "Overview" && (
            <div>
              <div className="sec-head" style={{ fontSize: 15 }}>Revenue by sport</div>
              <Bars rows={snap.sports} labelKey="sport" valueKey="revenue" />
              <div className="sec-head" style={{ fontSize: 15, marginTop: 24 }}>Revenue by channel</div>
              <Bars rows={snap.channels.map((c) => ({ channel: c.channel, revenue: c.value, deals: c.count }))} labelKey="channel" valueKey="revenue" />
            </div>
          )}

          {tab === "Clients" && (
            <DataTable rows={snap.clients} initialSort={{ key: "revenue", dir: -1 }}
              filters={[{ key: "channel", label: "channels" }]}
              columns={[
                { key: "client", label: "Client" },
                { key: "revenue", label: "Revenue", num: true, render: (r) => money(r.revenue) },
                { key: "invoices", label: "Invoices", num: true },
                { key: "identities", label: "#IDs", num: true },
                { key: "channel", label: "Channel", render: (r) => <Pill value={r.channel} /> },
                { key: "emails", label: "Emails", render: (r) => <span style={{ color: "var(--text-dim,#8b97a7)", fontSize: 12 }}>{(r.emails || []).join(", ")}</span> },
              ]} />
          )}

          {tab === "Athletes" && (
            <DataTable rows={snap.athletes} initialSort={{ key: "revenue", dir: -1 }}
              filters={[{ key: "sport", label: "sports" }]}
              columns={[
                { key: "athlete", label: "Athlete" },
                { key: "sport", label: "Sport" },
                { key: "revenue", label: "Revenue", num: true, render: (r) => money(r.revenue) },
                { key: "deals", label: "Deals", num: true },
              ]} />
          )}

          {tab === "Sports" && (
            <DataTable rows={snap.sports} initialSort={{ key: "revenue", dir: -1 }}
              columns={[
                { key: "sport", label: "Sport" },
                { key: "revenue", label: "Revenue", num: true, render: (r) => money(r.revenue) },
                { key: "deals", label: "Athlete-deals", num: true },
                { key: "athletes", label: "#Athletes", num: true },
              ]} />
          )}

          {tab === "Pipeline" && (
            <DataTable rows={withStage(snap.inquiries)} initialSort={{ key: "created", dir: -1 }}
              filters={[{ key: "class", label: "classes" }, { key: "stage_view", label: "stages" }, { key: "channel", label: "channels" }]}
              columns={[
                { key: "class", label: "Class", render: (r) => <Pill value={r.class} /> },
                { key: "channel", label: "Channel", render: (r) => <Pill value={r.channel} /> },
                { key: "stage_view", label: "Stage", render: (r) => (
                  <span style={{ color: STAGE_COLOR[r.stage_view] }}
                    title={r.crm_stage ? "HubSpot deal stage" : "Cockpit estimate (not a HubSpot deal)"}>
                    {r.stage_view}
                  </span>) },
                { key: "created", label: "Date" },
                { key: "name", label: "Name" },
                { key: "company", label: "Company" },
                { key: "booking_type", label: "Type" },
                { key: "website", label: "Website", render: (r) => r.website ? <span style={{ color: "var(--text-dim,#8b97a7)", fontSize: 12 }}>{r.website}</span> : "" },
                { key: "email", label: "Email" },
                { key: "speaker", label: "Speaker" },
                { key: "fee_raw", label: "Fee" },
                { key: "flags", label: "Flags", render: (r) => <span style={{ color: "#f59e0b", fontSize: 11 }}>{r.flags}</span> },
                { key: "comments", label: "Message", render: (r) => <span title={r.comments} style={{ color: "var(--text-dim,#8b97a7)", fontSize: 12, display: "inline-block", maxWidth: 360 }}>{r.comments}</span> },
              ]} />
          )}

          {tab === "Duplicates" && (
            <DataTable rows={snap.duplicates} initialSort={{ key: "revenue", dir: -1 }}
              columns={[
                { key: "client", label: "Client" },
                { key: "revenue", label: "Combined revenue", num: true, render: (r) => money(r.revenue) },
                { key: "invoices", label: "Invoices", num: true },
                { key: "identities", label: "Duplicate identities", render: (r) => (r.identities || []).join("  •  ") },
              ]} />
          )}

          {tab === "Invoices" && (
            <DataTable rows={snap.invoices} initialSort={{ key: "date", dir: -1 }}
              filters={[{ key: "channel", label: "channels" }]}
              columns={[
                { key: "doc", label: "Doc#" },
                { key: "date", label: "Date" },
                { key: "client", label: "Client" },
                { key: "channel", label: "Channel", render: (r) => <Pill value={r.channel} /> },
                { key: "amount", label: "Amount", num: true, render: (r) => money(r.amount) },
                { key: "balance", label: "Balance", num: true, render: (r) => money(r.balance) },
                { key: "desc", label: "Description", render: (r) => <span style={{ color: "var(--text-dim,#8b97a7)", fontSize: 12 }}>{r.desc}</span> },
              ]} />
          )}

          {tab === "Profitability" && (
            <ProfitabilityTab prof={prof} blendedRate={blendedRate} setBlendedRate={setBlendedRate} />
          )}
        </>
      )}
    </div>
  );
}
